import { ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { NgIf, NgClass, NgStyle } from '@angular/common';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ResponseAnalyticsService } from 'src/app/shared/services/response-analytics/response-analytics.service';
import { HelpersService } from 'src/app/shared/services/helpers/helpers.service';
import { DownloadService } from 'src/app/shared/services/downloads/download.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { ScoreDropdownMenuComponent } from '../score-dropdown-menu/score-dropdown-menu.component';
import { LtmsLoaderComponent } from '../../ltms-loader/ltms-loader.component';
import { LtmsErrorComponent } from '../../ltms-error/ltms-error.component';
import { ScoreWidgetComponent } from '../score-widget/score-widget.component';
import { ScoreTableComponent } from '../score-table/score-table.component';
import { ScoreLineChartComponent } from '../score-line-chart/score-line-chart.component';
import { ScoreBarChartComponent } from '../score-bar-chart/score-bar-chart.component';
import { ScoreComparisonToggleComponent } from '../score-comparison-toggle/score-comparison-toggle.component';
import {
  getComparisonPeriodLabel,
  getShowComparisonFlag,
  isComparisonAvailable,
  setShowComparisonFlag,
  shouldShowDeviation,
} from 'src/app/shared/utils/score-deviation.util';
import { applyComparisonApiDateRange } from 'src/app/shared/utils/comparison-api-date-range.util';

@Component({
  selector: 'app-score-custom',
  templateUrl: './score-custom.component.html',
  styleUrl: './score-custom.component.scss',
  imports: [
    NgIf,
    NgClass,
    NgStyle,
    NgbTooltip,
    ScoreDropdownMenuComponent,
    ScoreComparisonToggleComponent,
    LtmsLoaderComponent,
    LtmsErrorComponent,
    ScoreWidgetComponent,
    ScoreTableComponent,
    ScoreLineChartComponent,
    ScoreBarChartComponent,
  ],
})
export class ScoreCustomComponent implements OnInit, OnDestroy {
  mainTabDescription =
    'View individual conversation questions and their responses, including NPS metrics and qualitative feedback, for granular customer insights.';
  scoreDescription =
    'Explore detailed insights into the overall score and response distribution across different dimensions. Click the plus icon to expand a row, or select a row to filter the trend charts.';
  scoreTrendsDescription =
    'Track score trends with adjustable time views (daily, weekly, etc.), showing overall data or data for a selected row. Hover to view scores and distribution for specific time intervals.';
  responseTrendDescription =
    'Analyze the overtime response trend with adjustable time views (daily, weekly, etc.), displaying overall data or data for a selected row. Hover to see details for specific time intervals.';

  isQuestionGroupAvailable = false;
  isQuestionGroupMode = true;
  hover = false;
  hasGroupSelection = false;
  selectedOption = '';
  viewDropdownFieldId: any = '';
  viewByDropdown: any = {
    config: { width: '280px', tab: 'question' },
    selectedOption: '',
    options: [],
  };
  viewTypeDropdown: any = {
    config: { width: '128px', isDisabled: true, tab: 'scoreViewtype' },
    selectedOption: '',
    options: [],
  };
  viewTypeDropdownSelectedName = '';
  viewTypeDropdownSelectedFieldId = '';
  viewTypeDropdownSelectedFieldType = '';
  viewTypeDropdownSelectedDataType: any = '';

  showComparisonFlag = false;
  isComparisonAvailable = true;
  dateType = 'period';
  comparisonPeriodLabel = '';
  readonly tablePlugins = { deviationAlignment: true };
  readonly csatDistributionBands = { top: [5], mid: [4], bottom: [1, 2, 3] };
  readonly csatHeaderLabels = {
    csat: 'CSAT',
    responses: 'Responses',
    top: 'TB',
    mid: 'MB',
    bottom: 'B3B',
    display: 'CSAT',
  };

  fullScreenComponent = '';
  isFullScreen = false;

  lhsBoxLoader = true;
  lhsBoxError = false;
  scoreTable: any = { header: [], row: [] };
  cohortTable: any = { header: [], row: [] };
  cohortTableLoader = false;
  cohortTableError = false;
  l2Table: any = { header: [], row: [] };
  l2TableLoader = false;
  l2TableError = false;
  textHighLightId: any = '';
  selectedChartFilters: any[] = [];
  lastCrossTabResponse: any = null;
  groupHeaders: any[] = [];
  private l2TableRequestId = 0;
  private subtagMapper: Record<string, { ids: string[] }> = {};

  lineChartDisplayName = 'Rating';
  chartName = '';
  lineChartLoader = true;
  lineChartError = false;
  lineChartTrendType: any = '';
  lineChartDisplay = '';
  lineChartData: any = {};
  lineChartDataCache: any = [];
  lineChartPlugins: any = {
    lineColor: '#2CA02C',
    gradientStartColor: '#2CA02C',
    gradientEndColor: '#2CA02C0F',
    borderWidth: 2,
    pointBorderWidth: 2,
    pointRadius: 6,
    pointBorderColor: '#ffffff',
  };

  isBarChartTypePercentage = false;
  barChartLoader = true;
  barChartError = false;
  barChartTrendType: any = '';
  barChartDisplay = '';
  barChartPlugins: any = { yText: 'Responses' };
  barChartData: any = {};
  barChartDataCache: any = [];

  private destroy$ = new Subject<void>();
  private lineChartTrigger$ = new Subject<void>();
  private barChartTrigger$ = new Subject<void>();

  constructor(
    private responseAnalyticsService: ResponseAnalyticsService,
    private helpersService: HelpersService,
    private downloadService: DownloadService,
    private toastService: ToastService,
    private changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.refreshComparisonLabels();
    this.refreshQuestionGroupAvailability();
    this.prepareQuestionGroup();
    this.viewTypeDropdown.config.width = this.calculateViewButtonWidth();
    this.applyCohortDropdown(true);
    this.lineChartTrigger$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.callApiForLineChart();
    });
    this.barChartTrigger$.pipe(debounceTime(100), takeUntil(this.destroy$)).subscribe(() => {
      this.callApiForBarChart();
    });
    this.helpersService.appState.pipe(takeUntil(this.destroy$)).subscribe((response) => {
      if (response && response.pending === false) {
        this.runAfterCurrentChangeDetection(() => {
          this.refreshComparisonLabels();
          this.refreshQuestionGroupAvailability();
          if (!this.viewByDropdown.options.length) {
            if (this.isQuestionGroupMode) {
              this.prepareQuestionGroup();
            } else {
              this.prepareSummaryQuestionDropdown();
            }
          }
          this.applyCohortDropdown(false);
          this.fetchTabData();
        });
      }
    });
    this.fetchTabData();
  }

  scoreWidget(type: string): any {
    return {
      isFullScreenButtonShow: true,
      isDownloadButtonShow: true,
      isTrendDropdownShow: type === 'lineChart' || type === 'barChart',
      isAbsoluteChartShow: type === 'barChart',
      isPercenttageChartShow: type === 'barChart',
    };
  }

  questionGroupClick(): void {
    if (!this.isQuestionGroupAvailable) {
      return;
    }
    this.isQuestionGroupMode = !this.isQuestionGroupMode;
    this.textHighLightId = '';
    this.selectedChartFilters = [];
    if (this.isQuestionGroupMode) {
      this.prepareQuestionGroup();
    } else {
      this.prepareSummaryQuestionDropdown();
    }
    this.applyCohortDropdown(true);
    this.fetchTabData();
  }

  onOptionChange(event: any): void {
    this.viewDropdownFieldId = event?.field_id;
    this.viewByDropdown.selectedOption = event?.name;
    this.selectedOption = event?.name || '';
    this.setViewByFullTitle(event?.name || '');
    this.hasGroupSelection = true;
    this.textHighLightId = '';
    this.selectedChartFilters = [];
    this.chartName = event?.name || this.getProjectName();
    this.applyCohortDropdown(true);
    this.fetchTabData();
  }

  onViewTypeChange(event: any): void {
    this.setSelectedCohort(event);
    this.selectedChartFilters = [];
    this.textHighLightId = this.scoreTable.row[0]?.key || '';
    this.chartName = this.scoreTable.row[0]?.name || this.getProjectName();
    if (this.lastCrossTabResponse) {
      this.prepareCohortTable(this.lastCrossTabResponse);
    }
    this.loadCombinedL2Table();
    this.lineChartTrigger$.next();
    this.barChartTrigger$.next();
  }

  onShowComparisonChange(checked: boolean): void {
    this.showComparisonFlag = checked;
    setShowComparisonFlag(checked);
    if (this.lastCrossTabResponse) {
      this.prepareGroupTable(this.lastCrossTabResponse);
    }
  }

  onTableRowClick(row: any): void {
    if (!row?.key) {
      return;
    }
    this.textHighLightId = row.key;
    this.chartName = row.name || this.getProjectName();
    this.selectedChartFilters = Array.isArray(row.chartSources) ? row.chartSources : [];
    this.lineChartTrigger$.next();
    this.barChartTrigger$.next();
  }

  onFullScreen(_event: any, cardName: string): void {
    this.isFullScreen = !this.isFullScreen;
    this.fullScreenComponent = this.isFullScreen ? cardName : '';
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.isFullScreen = false;
    this.fullScreenComponent = '';
  }

  onOverlayClick(): void {
    this.isFullScreen = false;
    this.fullScreenComponent = '';
  }

  onTrendChange(event: any, cardName: string): void {
    if (cardName === 'lineChart') {
      this.lineChartDisplay = event?.display || '';
      this.lineChartTrendType = event?.trend_type;
      this.lineChartTrigger$.next();
    }
    if (cardName === 'barChart') {
      this.barChartDisplay = event?.display || '';
      this.barChartTrendType = event?.trend_type;
      this.barChartTrigger$.next();
    }
  }

  onBarChartTypeChange(event: any): void {
    this.isBarChartTypePercentage = event;
    if (_.size(this.barChartDataCache) === 0) {
      this.barChartError = true;
      this.barChartLoader = false;
      return;
    }
    this.barChartData = this.generateBarChartConfig(this.barChartDataCache);
  }

  onDownload(cardName: string): void {
    if (cardName === 'drillDownTable') {
      this.downloadTable();
      return;
    }
    this.downloadChart(cardName);
  }

  private fetchTabData(): void {
    if (!this.viewByDropdown.options.length || !this.viewDropdownFieldId) {
      this.hasGroupSelection = false;
      this.lhsBoxLoader = false;
      this.lhsBoxError = true;
      this.resetCohortTable(true);
      this.resetL2Table(true);
      return;
    }
    this.hasGroupSelection = true;
    this.callGetcrossTabAnalytics();
    this.lineChartTrigger$.next();
    this.barChartTrigger$.next();
  }

  private refreshQuestionGroupAvailability(): void {
    const questionGroup: any = _.get(Helpers, 'brandDetails.dynamic_properties.question_group', null) || [];
    const detailedFeedbackItems = this.getDetailedFeedbackItems();
    this.isQuestionGroupAvailable = questionGroup.some((items: any) =>
      detailedFeedbackItems.some((item: any) =>
        item.dynamic_properties?.question_group?.some((group: any) => group.id === items?.id)
      )
    );
  }

  private setViewBySelection(options: any[]): void {
    this.viewByDropdown.options = options;
    const firstOption = options[0];
    this.viewByDropdown.selectedOption = firstOption?.name || '';
    this.viewDropdownFieldId = firstOption?.field_id || '';
    this.selectedOption = firstOption?.name || '';
    this.chartName = firstOption?.name || this.getProjectName();
    this.setViewByFullTitle(firstOption?.name || '');
  }

  private setViewByFullTitle(name: string): void {
    this.viewByDropdown.config = {
      ...this.viewByDropdown.config,
      selectedFullTitle: name,
    };
  }

  private prepareQuestionGroup(): void {
    const questionGroup: any = _.get(Helpers, 'brandDetails.dynamic_properties.question_group', null) || [];
    const detailedFeedbackItems = this.getDetailedFeedbackItems();
    const options: any[] = [];
    questionGroup.forEach((items: any) => {
      const result = detailedFeedbackItems.find((item: any) =>
        item.dynamic_properties?.question_group?.some((group: any) => group.id === items?.id)
      );
      if (_.size(result) > 0) {
        options.push({ name: items?.name, field_id: items?.id, type: result?.type || 'rating' });
      }
    });
    this.setViewBySelection(options);
  }

  private prepareSummaryQuestionDropdown(): void {
    const options = this.getDetailedFeedbackItems()
      .filter((item: any) =>
        item?.is_displayed_on_summary &&
        !item?.is_deleted &&
        Array.isArray(item?.dynamic_properties?.question_group) &&
        item.dynamic_properties.question_group.length > 0
      )
      .map((item: any) => ({
        name: item?.text || item?.question || item?.field_id,
        field_id: item?.field_id,
        type: item?.type || 'rating',
      }));
    this.setViewBySelection(options);
  }

  getCohort(): any[] {
    const cohorts: any[] = [];
    if (this.isQuestionGroupMode) {
      const subtagMapperKey = this.getSubtagMapperKeyForSelectedGroup();
      if (this.viewDropdownFieldId && subtagMapperKey && this.subtagMapper[subtagMapperKey]?.ids?.length) {
        cohorts.push({
          name: subtagMapperKey,
          field_id: `${this.viewDropdownFieldId}_subtags`,
          field_type: 'subtags',
          question_group_id: this.viewDropdownFieldId,
          isSubtags: true,
          child: null,
          data_type: null,
        });
      } else if (this.viewDropdownFieldId) {
        const selectedQuestionName = this.selectedOption || this.viewByDropdown.selectedOption;
        cohorts.push({
          name: `${selectedQuestionName} Subtags`,
          field_id: `${this.viewDropdownFieldId}_subtags`,
          field_type: 'subtags',
          question_group_id: this.viewDropdownFieldId,
          isSubtags: true,
          child: null,
          data_type: null,
        });
      }
    }
    const fieldDefs = _.get(Helpers, 'brandDetails.field_definitions', []);
    if (fieldDefs && fieldDefs.length > 0) {
      fieldDefs.forEach((field: any) => {
        if (field?.field_type === 'hierarchy') {
          return;
        }
        if (field?.compare_filter || field?.field_type === 'response') {
          cohorts.push(this.createCohortOption(field));
        }
      });
    }
    return cohorts;
  }

  private createCohortOption(field: any): any {
    const name =
      field.name ||
      field.text ||
      field.question ||
      field.label ||
      field.field_id;
    return {
      name,
      field_id: field.field_id,
      field_type: field.field_type || 'tag',
      child: field?.child ? this.processedCohortChild(field) : null,
      data_type: field.data_type || null,
      view_type: field.view_type || null,
    };
  }

  private processedCohortChild(childObj: any): any[] {
    const child: any[] = [];
    const fieldDefinitions = _.get(Helpers, 'brandDetails.field_definitions', []);
    const filteredFieldDefinition = fieldDefinitions.find((item: any) => item.field_id === childObj.field_id) || {};
    const detailedFeedbackItems = this.getDetailedFeedbackItems();
    const detailedFeedbackItem = _.find(detailedFeedbackItems, { field_id: childObj.field_id }, null);
    const newChild: any = {
      field_id: childObj.field_id,
      field_type: childObj?.field_type ?? detailedFeedbackItem?.field_type ?? 'tag',
      data_type: filteredFieldDefinition?.data_type ?? null,
      name:
        filteredFieldDefinition?.name ||
        filteredFieldDefinition?.text ||
        filteredFieldDefinition?.question ||
        filteredFieldDefinition?.label ||
        detailedFeedbackItem?.question ||
        detailedFeedbackItem?.text ||
        filteredFieldDefinition?.field_id ||
        childObj.field_id,
      view_type: filteredFieldDefinition?.view_type ?? null,
    };
    if (childObj?.field_type === 'response') {
      newChild.name = detailedFeedbackItem?.question || detailedFeedbackItem?.text || newChild.name;
      newChild.field_sub_type = detailedFeedbackItem?.type;
      newChild.possible_values = detailedFeedbackItem?.possible_values;
    }
    child.push(newChild);
    if (childObj.child) {
      return child.concat(this.processedCohortChild(childObj.child));
    }
    return child;
  }

  private applyCohortDropdown(resetSelection: boolean): void {
    this.buildSubtagMapper();
    const cohorts = this.getCohort();
    this.viewTypeDropdown.options = cohorts;
    this.viewTypeDropdown.config = {
      ...this.viewTypeDropdown.config,
      width: this.calculateViewButtonWidth(),
      isDisabled: cohorts.length === 0,
    };
    if (!cohorts.length) {
      this.viewTypeDropdown.selectedOption = '';
      this.viewTypeDropdownSelectedName = '';
      this.viewTypeDropdownSelectedFieldId = '';
      this.viewTypeDropdownSelectedFieldType = '';
      this.viewTypeDropdownSelectedDataType = '';
      return;
    }
    const currentStillValid =
      !resetSelection &&
      cohorts.some((option: any) => option.field_id === this.viewTypeDropdownSelectedFieldId);
    const selected = currentStillValid
      ? cohorts.find((option: any) => option.field_id === this.viewTypeDropdownSelectedFieldId)
      : this.getDefaultCohort(cohorts);
    this.setSelectedCohort(selected);
  }

  private getDefaultCohort(cohorts: any[]): any {
    if (!this.isQuestionGroupMode) {
      const origin = cohorts.find(
        (option: any) => String(option?.name || '').trim().toLowerCase() === 'origin'
      );
      if (origin) {
        return origin;
      }
    }
    return cohorts[0];
  }

  private setSelectedCohort(option: any): void {
    if (!option) {
      return;
    }
    this.viewTypeDropdown.selectedOption = option.name;
    this.viewTypeDropdownSelectedName = option.name;
    this.viewTypeDropdownSelectedFieldId = option.field_id;
    this.viewTypeDropdownSelectedFieldType = option.field_type || 'tag';
    this.viewTypeDropdownSelectedDataType = option.data_type || null;
  }

  calculateViewButtonWidth(): string {
    const minScreen = 1366;
    const maxScreen = 1920;
    const minWidth = 128;
    const maxWidth = 280;
    const screenWidth = window.innerWidth;
    if (screenWidth <= minScreen) {
      return `${minWidth}px`;
    }
    if (screenWidth >= maxScreen) {
      return `${maxWidth}px`;
    }
    const width = minWidth + ((screenWidth - minScreen) * (maxWidth - minWidth)) / (maxScreen - minScreen);
    return `${Math.round(width)}px`;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewTypeDropdown.config.width = this.calculateViewButtonWidth();
  }

  private callGetcrossTabAnalytics(): void {
    this.lhsBoxLoader = true;
    this.lhsBoxError = false;
    this.resetCohortTable(false);
    this.resetL2Table(false);
    this.cohortTableLoader = true;
    this.l2TableLoader = true;
    let payload: any = {
      manager_type: 2,
      brand_id: _.get(Helpers, 'brandDetails._id', null),
      main_rater_type: _.get(Helpers, 'brandDetails.main_rater_type', 2),
      shop_id_list: [],
      trend_type: _.get(Helpers, 'datePicker.selectedTrend', null),
      from: _.get(Helpers, 'datePicker.startDate', null),
      to: _.get(Helpers, 'datePicker.endDate', null),
      is_custom_date_range_selected: _.get(Helpers, 'datePicker.isCustomDateRangeSelected', false),
      universal_filter: _.get(Helpers, 'universalFilter', null),
      hierarchy_stack: _.get(Helpers, 'hierarchyStack', null),
      group_alias_key: this.getGroupAliasKey(),
    };
    payload = _.omitBy(payload, _.isNil);
    this.applyComparisonApiDateRange(payload);
    this.responseAnalyticsService
      .getcrossTabAnalytics(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        if (response?.message === 'Success') {
          this.lastCrossTabResponse = response;
          this.prepareGroupTable(response);
        } else {
          this.lhsBoxLoader = false;
          this.lhsBoxError = true;
          this.resetCohortTable(true);
          this.resetL2Table(true);
        }
      });
  }

  private prepareGroupTable(response: any): void {
    const groups = this.buildGroupHeaders();
    this.groupHeaders = groups;
    const firstHeaderLabels = this.getHeaderLabelsForGroups(groups);
    this.lineChartDisplayName = firstHeaderLabels?.display || 'Rating';
    this.scoreTable.header = this.buildSimpleTableHeader(firstHeaderLabels);
    const rows: any[] = [];
    groups.forEach((header: any) => {
      if (header?.type === 'rating') {
        const calculatedData = this.prepareDataForAllGroup(header, response);
        if (this.getResponseCount(calculatedData) <= 0) {
          return;
        }
        rows.push({
          key: header.id,
          name: header.mainTableName,
          ...this.buildCsatTableCells(calculatedData),
          deviations: this.mapDeviations(this.computeGroupRowDeviations(header, response)),
        });
      }
    });
    this.scoreTable.row = rows;
    this.lhsBoxLoader = false;
    this.lhsBoxError = rows.length === 0;
    if (rows.length > 0 && !this.textHighLightId) {
      this.textHighLightId = rows[0].key;
      this.chartName = rows[0].name || this.getProjectName();
    } else if (!this.chartName) {
      this.chartName = this.getProjectName();
    }
    this.prepareCohortTable(response);
    this.loadCombinedL2Table();
  }

  private resetCohortTable(markError: boolean): void {
    this.cohortTable = { header: [], row: [] };
    this.cohortTableLoader = false;
    this.cohortTableError = markError;
  }

  private resetL2Table(markError: boolean): void {
    this.l2TableRequestId += 1;
    this.l2Table = { header: [], row: [] };
    this.l2TableLoader = false;
    this.l2TableError = markError;
  }

  private isSubtagsCohortSelected(): boolean {
    return (
      this.viewTypeDropdownSelectedFieldType === 'subtags' ||
      !!_.find(this.viewTypeDropdown.options, {
        field_id: this.viewTypeDropdownSelectedFieldId,
        isSubtags: true,
      })
    );
  }

  private prepareCohortTable(response: any): void {
    this.cohortTableLoader = true;
    this.cohortTableError = false;
    const headerLabels = this.getHeaderLabelsForGroups(this.groupHeaders);
    this.cohortTable.header = this.buildSimpleTableHeader(headerLabels, 'Question');
    this.cohortTable.row = this.buildL1QuestionRows(response);
    this.cohortTableLoader = false;
    this.cohortTableError = this.cohortTable.row.length === 0;
  }

  private getDetailedFeedbackItems(): any[] {
    return (
      _.get(Helpers, 'brandDetails.detailed_feedback_items', null) ||
      _.get(Helpers, 'brandDetails.details_field_items', null) ||
      []
    );
  }

  private getFieldDefinitions(): any[] {
    return _.get(Helpers, 'brandDetails.field_definitions', []) || [];
  }

  private getQuestionsInSelectedGroup(): any[] {
    const selectedId = this.viewDropdownFieldId;
    if (!selectedId) {
      return [];
    }
    if (!this.isQuestionGroupMode) {
      const selectedQuestion = this.getDetailedFeedbackItems().find((item: any) => item?.field_id === selectedId);
      return selectedQuestion ? [selectedQuestion] : [];
    }
    return _.filter(this.getDetailedFeedbackItems(), (obj: any) =>
      _.some(obj?.dynamic_properties?.question_group, { id: selectedId })
    );
  }

  private getL1QuestionsForSelectedGroup(): any[] {
    return this.getQuestionsInSelectedGroup().filter(
      (item: any) => !item?.type || item.type === 'rating' || item.type === 'text'
    );
  }

  private getSelectedGroupL1Ids(): any[] {
    return this.getL1QuestionsForSelectedGroup()
      .filter((item: any) => item?.type !== 'text')
      .map((item: any) => ({
        field_id: item?.field_id || null,
        question_id: item?.question_id || item?.id || null,
        type: item?.type || null,
        question: item?.question || item?.text || null,
      }));
  }

  private buildL1QuestionRows(response: any): any[] {
    return this.buildSubtagsCohortRows(response);
  }

  private buildSubtagsCohortRows(response: any): any[] {
    const currentDetailedRating = _.get(_.find(response?.data?.response, { key: 'current' }), 'detailed_rating', []) || [];
    const previousDetailedRating = _.get(_.find(response?.data?.response, { key: 'previous' }), 'detailed_rating', []) || [];
    const l1Questions = this.getL1QuestionsForSelectedGroup();
    const rows: any[] = [];
    l1Questions.forEach((item: any) => {
      if (item?.type === 'text') {
        return;
      }
      const metrics = this.computeQuestionMetrics(item, currentDetailedRating);
      if (this.getResponseCount(metrics) <= 0) {
        return;
      }
      rows.push({
        key: item.field_id,
        name: item?.text || item?.question || item?.field_id,
        ...this.buildCsatTableCells(metrics),
        deviations: this.mapDeviations(this.computeQuestionRowDeviations(item, currentDetailedRating, previousDetailedRating)),
      });
    });
    return rows;
  }

  private getMultiSelectChildren(parentFieldId: any): any[] {
    if (!parentFieldId) {
      return [];
    }
    return this.getDetailedFeedbackItems()
      .filter((item: any) => item?.group_id == parentFieldId)
      .sort((left: any, right: any) => Number(left?.value || 0) - Number(right?.value || 0));
  }

  private getL2ParentByFieldId(fieldId: any): any {
    if (!fieldId) {
      return null;
    }
    const items = this.getDetailedFeedbackItems();
    return (
      items.find((item: any) => item.field_id === fieldId) ||
      this.getFieldDefinitions().find((field: any) => field.field_id === fieldId) ||
      { field_id: fieldId, type: 'multi-select', field_type: 'response' }
    );
  }

  private toCohortQueryField(fieldId: any): any {
    const selectedItem =
      this.getDetailedFeedbackItems().find((item: any) => item.field_id == fieldId) ||
      this.getFieldDefinitions().find((field: any) => field.field_id == fieldId) ||
      this.getL2ParentByFieldId(fieldId);
    return {
      ...(selectedItem || {}),
      field_id: fieldId,
      field_type: selectedItem?.field_type || 'response',
      data_type: selectedItem?.data_type || null,
      type: selectedItem?.type,
    };
  }

  private getMaintagFieldId(): string | null {
    const maintag = this.getDetailedFeedbackItems().find(
      (item: any) => String(item?.question || item?.text || '').trim() === 'Maintag'
    );
    return maintag?.field_id ? String(maintag.field_id) : null;
  }

  private getMaintagL1Drivers(): any[] {
    const maintagFieldId = this.getMaintagFieldId();
    if (!maintagFieldId) {
      return [];
    }
    return this.getDetailedFeedbackItems().filter((item: any) => item?.group_id == maintagFieldId);
  }

  private getL2DriverParents(): any[] {
    return this.getDetailedFeedbackItems().filter((item: any) => {
      const label = String(item?.question || item?.text || '');
      return (
        label.includes('L2 driver') &&
        item?.type === 'multi-select' &&
        !item?.group_id &&
        item?.field_id &&
        !item?.is_deleted
      );
    });
  }

  private buildSubtagMapper(): void {
    const brandDetails: any = _.get(Helpers, 'brandDetails', null);
    // custom_tab_subtag_mapper is a brand detail field that is used to store the subtag mapper for the custom tab
    if (brandDetails?.['custom_tab_subtag_mapper']) {
      this.subtagMapper = brandDetails['custom_tab_subtag_mapper'];
      return;
    }
    // if custom_tab_subtag_mapper is not present, then build the subtag mapper using the maintag and l2 drivers
    const mapper: Record<string, { ids: string[] }> = {};
    const l1s = this.getMaintagL1Drivers();
    const l2s = this.getL2DriverParents();
    l1s.forEach((l1: any) => {
      const l1Text = String(l1?.text || l1?.question || '').trim();
      if (!l1Text) {
        return;
      }
      const mapperKey = `${l1Text} - subtags`;
      const ids = l2s
        .filter((l2: any) => String(l2?.text || l2?.question || '').includes(l1Text))
        .map((l2: any) => String(l2.field_id))
        .filter(Boolean);
      if (ids.length) {
        mapper[mapperKey] = { ids: _.uniq(ids) };
      }
    });
    this.subtagMapper = mapper;
  }

  private getMaintagL1ForSelectedGroup(): any | null {
    const groupName = String(this.selectedOption || this.viewByDropdown.selectedOption || '').toLowerCase();
    if (!groupName) {
      return null;
    }
    const matches = this.getMaintagL1Drivers().filter((l1: any) => {
      const l1Text = String(l1?.text || l1?.question || '').trim();
      return l1Text && groupName.includes(l1Text.toLowerCase());
    });
    if (!matches.length) {
      return null;
    }
    return _.maxBy(matches, (l1: any) => String(l1?.text || l1?.question || '').length);
  }

  private getSubtagMapperKeyForSelectedGroup(): string | null {
    const l1 = this.getMaintagL1ForSelectedGroup();
    if (!l1) {
      return null;
    }
    const l1Text = String(l1?.text || l1?.question || '').trim();
    return l1Text ? `${l1Text} - subtags` : null;
  }

  private getGroupedL2FieldIdsForSelectedGroup(): string[] {
    const mapperKey = this.getSubtagMapperKeyForSelectedGroup();
    if (!mapperKey) {
      return [];
    }
    return this.subtagMapper[mapperKey]?.ids || [];
  }

  private getGroupedL2QueryFields(): any[] {
    const fieldIds = this.getGroupedL2FieldIdsForSelectedGroup();
    return fieldIds.map((fieldId: string) => this.toCohortQueryField(fieldId));
  }

  private getCohortQueryFields(): any[] {
    if (this.isSubtagsCohortSelected()) {
      return this.getGroupedL2QueryFields();
    }
    const selectedFieldId = this.viewTypeDropdownSelectedFieldId;
    if (!selectedFieldId) {
      return [];
    }
    const selectedItem =
      this.getDetailedFeedbackItems().find((item: any) => item.field_id === selectedFieldId) ||
      this.getFieldDefinitions().find((field: any) => field.field_id === selectedFieldId);
    return [
      {
        ...(selectedItem || {}),
        field_id: selectedFieldId,
        field_type: selectedItem?.field_type || this.viewTypeDropdownSelectedFieldType || 'tag',
        data_type: selectedItem?.data_type || this.viewTypeDropdownSelectedDataType || null,
        type: selectedItem?.type,
        groupL2Fields: this.getGroupedL2QueryFields(),
      },
    ];
  }

  private getFieldProps(fieldItem: any): any {
    const items = this.getDetailedFeedbackItems();
    const detailedItem = items.find((item: any) => item.field_id === fieldItem?.field_id) || fieldItem;
    if (detailedItem?.type === 'rating' || detailedItem?.type === 'single-select') {
      return detailedItem?.prop || {};
    }
    if (detailedItem?.type === 'multi-select' || fieldItem?.type === 'multi-select') {
      const props: any = {};
      items
        .filter((item: any) => item?.group_id === fieldItem?.field_id)
        .forEach((item: any) => {
          props[item.field_id] = item?.question || item?.text;
          if (item?.value != null) {
            props[item.value] = item?.question || item?.text;
          }
        });
      return props;
    }
    return detailedItem?.prop || {};
  }

  private buildComparisonPayloadForField(fieldItem: any): any {
    const detailedItem =
      this.getDetailedFeedbackItems().find((item: any) => item.field_id === fieldItem?.field_id) || fieldItem;
    const children = this.getMultiSelectChildren(fieldItem?.field_id);
    const isMultiSelect = detailedItem?.type === 'multi-select' || children.length > 0;
    const selectedType = fieldItem?.field_type || this.viewTypeDropdownSelectedFieldType || detailedItem?.field_type;
    const apiFieldType =
      isMultiSelect || selectedType === 'response'
        ? 'response'
        : selectedType === 'hierarchy'
          ? 'hierarchy'
          : 'tag';
    const base = this.helpersService.getInitialQueryParameters() || {};
    let payload: any = {
      ...base,
      shop_id_list: base.shop_id_list ?? [],
      from: _.get(Helpers, 'datePicker.startDate', null),
      to: _.get(Helpers, 'datePicker.endDate', null),
      question_aggs: true,
      is_custom_date_range_selected: _.get(Helpers, 'datePicker.isCustomDateRangeSelected', false),
      date_filter_type: _.get(Helpers, 'datePicker.dateFilterType', null),
      universal_filter: _.get(Helpers, 'universalFilter', null),
      hierarchy_stack: _.get(Helpers, 'hierarchyStack', null),
      group_alias_key: this.getGroupAliasKey(),
      view_type: 'list',
      field_id: fieldItem.field_id,
      field_type: apiFieldType,
      size: 100,
    };
    const fieldDataType = fieldItem?.data_type || this.viewTypeDropdownSelectedDataType;
    if (fieldDataType) {
      payload.field_data_type = fieldDataType;
    }
    const groupL2Filters = this.buildGroupL2Filters(fieldItem?.groupL2Fields);
    if (groupL2Filters.length === 1) {
      payload.filters = groupL2Filters;
    } else if (groupL2Filters.length > 1) {
      payload.filters = [{ $or: groupL2Filters }];
    }
    if (isMultiSelect) {
      payload.field_type = 'response';
      payload.field_sub_type = 'multi-select';
      payload.possible_values = children.map((item: any) => ({
        field_id: item?.field_id,
        value: item?.value,
      }));
    } else if (['rating', 'single-select'].includes(detailedItem?.type)) {
      payload.field_type = 'response';
      payload.field_sub_type = detailedItem.type;
      payload.possible_values = detailedItem?.possible_values;
    }
    payload = _.omitBy(payload, (value) => _.isNil(value) || value === '');
    this.applyComparisonApiDateRange(payload);
    return payload;
  }

  private buildGroupL2Filters(groupL2Fields: any[]): any[] {
    return (groupL2Fields || [])
      .filter((item: any) => item?.field_id && !item?.is_deleted)
      .map((item: any) => {
        const children = this.getMultiSelectChildren(item.field_id);
        return {
          field_id: item.field_id,
          field_type: 'response',
          field_sub_type: 'multi-select',
          possible_values: children.map((child: any) => ({
            field_id: child?.field_id,
            value: child?.value,
          })),
        };
      });
  }

  private fetchComparisonResponsesInParallel(payloads: any[]): Promise<PromiseSettledResult<{ payload: any; response: any }>[]> {
    // console.log('Payloads', payloads);
    const calls = payloads.map((payload) =>
      firstValueFrom(
        this.responseAnalyticsService.getcomparisonV2Data(payload).pipe(takeUntil(this.destroy$))
      ).then((response) => ({ payload, response }))
    );
    return Promise.allSettled(calls);
  }

  private getComparisonCurrentNode(entry: any): any {
    if (entry?.current !== undefined || entry?.previous !== undefined) {
      return entry.current || entry;
    }
    return entry;
  }

  private getComparisonPreviousNode(entry: any): any {
    return entry?.previous || null;
  }

  private flattenRatingDistribution(node: any): Record<string, number> {
    const mapped: Record<string, number> = {};
    if (!node || typeof node !== 'object') {
      return mapped;
    }
    if (Array.isArray(node.buckets)) {
      node.buckets.forEach((bucket: any) => {
        const rating = bucket?.key ?? bucket?.rating;
        const count = bucket?.doc_count || bucket?.number_of_user_rated || bucket?.number_of_users_rated || 0;
        if (rating != null) {
          mapped[String(rating)] = (mapped[String(rating)] || 0) + Number(count || 0);
        }
      });
    }
    Object.entries(node).forEach(([key, value]: any) => {
      if (key === 'buckets') {
        return;
      }
      if (typeof value === 'number' && !Number.isNaN(Number(key))) {
        mapped[key] = (mapped[key] || 0) + value;
      } else if (value && typeof value === 'object') {
        const count = value?.doc_count || value?.number_of_user_rated || value?.number_of_users_rated || 0;
        if (count) {
          mapped[key] = (mapped[key] || 0) + Number(count);
        }
      }
    });
    return mapped;
  }

  private getDistributionMap(node: any): Record<string, number> {
    const primary =
      node?.primary_rating_distribution ||
      node?.summary?.current?.primary_rating_distribution ||
      {};
    const mapped = this.flattenRatingDistribution(primary);
    if (Object.keys(mapped).length) {
      return mapped;
    }
    if (node?.primary_rating_details) {
      const details = Array.isArray(node.primary_rating_details)
        ? node.primary_rating_details
        : Object.values(node.primary_rating_details);
      details.forEach((detail: any) => {
        const rating = String(detail?.rating ?? detail?.key ?? '');
        const count = detail?.number_of_user_rated || detail?.number_of_users_rated || detail?.doc_count || 0;
        if (rating) {
          mapped[rating] = (mapped[rating] || 0) + Number(count || 0);
        }
      });
    }
    return mapped;
  }

  private getCsatDistributionFromNode(node: any): Record<string, number> {
    const merged: Record<string, number> = {};
    const l1Questions = this.getL1QuestionsForSelectedGroup().filter((item: any) => item?.type !== 'text');
    const ratingByType = node?.detailed_rating_by_type || {};
    l1Questions.forEach((item: any) => {
      const questionData = ratingByType[item.field_id];
      const ratingNumber = this.flattenRatingDistribution(
        questionData?.rating_number || questionData || {}
      );
      Object.entries(ratingNumber).forEach(([rating, count]) => {
        merged[rating] = (merged[rating] || 0) + Number(count || 0);
      });
    });
    if (Object.keys(merged).length) {
      return merged;
    }
    const detailedRating = node?.detailed_rating || node?.detailed_rating_details || [];
    if (Array.isArray(detailedRating) && detailedRating.length) {
      l1Questions.forEach((item: any) => {
        const fieldRating = detailedRating.find((entry: any) => entry?.field_id === item.field_id);
        const result = fieldRating?.detailed_rating || fieldRating?.detailed_rating_details;
        if (!result) {
          return;
        }
        const ratingEntries = Array.isArray(result) ? result : Object.values(result);
        ratingEntries.forEach((detail: any) => {
          const rating = String(detail?.rating ?? detail?.key ?? '');
          const count = detail?.number_of_user_rated || detail?.number_of_users_rated || detail?.doc_count || 0;
          if (rating) {
            merged[rating] = (merged[rating] || 0) + Number(count || 0);
          }
        });
      });
      if (Object.keys(merged).length) {
        return merged;
      }
    }
    return merged;
  }

  private getEntryResponseCount(node: any, distribution: Record<string, number>): number {
    const fromNode =
      node?.number_of_users_rated_primary_rating ??
      node?.number_of_user_rated ??
      node?.users_rater_primary_rating ??
      node?.doc_count;
    if (fromNode != null && Number(fromNode) > 0) {
      return Number(fromNode);
    }
    return Object.values(distribution || {}).reduce((sum: number, count: number) => sum + (Number(count) || 0), 0);
  }

  private computeCsatFromDistribution(distribution: Record<string, number>): any {
    const category = this.csatDistributionBands;
    let rawTop = 0;
    let rawMid = 0;
    let rawBottom = 0;
    let sumOfRating = 0;
    Object.entries(distribution || {}).forEach(([key, count]) => {
      const rating = Number(key);
      const numberOfUsersRated = Number(count) || 0;
      if (category.bottom.includes(rating)) {
        rawBottom += numberOfUsersRated;
      } else if (category.mid.includes(rating)) {
        rawMid += numberOfUsersRated;
      } else if (category.top.includes(rating)) {
        rawTop += numberOfUsersRated;
      }
      if (!isNaN(rating)) {
        sumOfRating += rating * numberOfUsersRated;
      }
    });
    const rawTotal = rawTop + rawMid + rawBottom;
    return {
      rawTotal,
      rawTop,
      rawMid,
      rawBottom,
      sumOfRating,
      rating: rawTotal > 0 ? parseFloat((sumOfRating / rawTotal).toFixed(2)) : 'N/A',
    };
  }

  private resolveL2OptionLabel(fieldItem: any, key: any): string {
    const children = this.getMultiSelectChildren(fieldItem?.field_id);
    const matchedChild = children.find(
      (item: any) => item?.field_id === key || String(item?.value) === String(key)
    );
    if (matchedChild) {
      return matchedChild?.question || matchedChild?.text || '';
    }
    const props = this.getFieldProps(fieldItem);
    const fromProps = props[key]?.label || props[key];
    if (fromProps) {
      return String(fromProps);
    }
    const feedbackItem = this.getDetailedFeedbackItems().find((item: any) => item?.field_id === key);
    const fromFeedback = feedbackItem?.question || feedbackItem?.text;
    if (fromFeedback) {
      return String(fromFeedback);
    }
    if (this.isSubtagsCohortSelected() || children.length || fieldItem?.type === 'multi-select') {
      return '';
    }
    return key != null && key !== '' ? String(key) : '';
  }

  private normalizeL2Label(label: any): string {
    return String(label || '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  private extractL2RowsFromComparisonResponse(response: any, fieldItem: any): any[] {
    const ratingSummary =
      response?.data?.RatingsSummary ||
      response?.data?.overall_summary_details?.overall_summary ||
      response?.data?.overall_summary ||
      {};
    const rows: any[] = [];
    Object.keys(ratingSummary || {}).forEach((key) => {
      const entry = ratingSummary[key];
      const currentNode = this.getComparisonCurrentNode(entry?.summary ? entry.summary : entry);
      const previousNode = this.getComparisonPreviousNode(entry?.summary ? entry.summary : entry);
      const currentDistribution = this.getCsatDistributionFromNode(currentNode);
      const previousDistribution = previousNode ? this.getCsatDistributionFromNode(previousNode) : {};
      const currentMetrics = this.computeCsatFromDistribution(currentDistribution);
      const previousMetrics = previousNode
        ? this.computeCsatFromDistribution(previousDistribution)
        : { rawTotal: 0, rawTop: 0, rawMid: 0, rawBottom: 0, sumOfRating: 0, rating: 'N/A' };
      if (this.getResponseCount(currentMetrics) <= 0) {
        return;
      }
      const label = this.resolveL2OptionLabel(fieldItem, key);
      if (!label) {
        return;
      }
      const children = this.getMultiSelectChildren(fieldItem?.field_id);
      const matchedChild = children.find(
        (item: any) => item?.field_id === key || String(item?.value) === String(key)
      );
      const isMultiSelect = children.length > 0 || fieldItem?.type === 'multi-select';
      const isResponseField = isMultiSelect || ['rating', 'single-select'].includes(fieldItem?.type);
      const numericKey = Number(key);
      rows.push({
        label,
        key,
        fieldId: fieldItem?.field_id,
        current: currentMetrics,
        previous: previousMetrics,
        chartSource: {
          field_id: fieldItem?.field_id,
          field_type: isResponseField ? 'response' : (fieldItem?.field_type === 'hierarchy' ? 'hierarchy' : 'tag'),
          field_sub_type: isMultiSelect ? 'multi-select' : (isResponseField ? fieldItem?.type : undefined),
          field_value: isMultiSelect
            ? (matchedChild?.value ?? (isNaN(numericKey) ? key : numericKey))
            : isResponseField
              ? (isNaN(numericKey) ? key : numericKey)
              : label,
          optionFieldId: matchedChild?.field_id || key,
          field_data_type: fieldItem?.data_type || this.viewTypeDropdownSelectedDataType || null,
          possible_values: isMultiSelect
            ? children.map((item: any) => ({ field_id: item?.field_id, value: item?.value }))
            : (fieldItem?.possible_values || []),
        },
      });
    });
    return rows;
  }

  private combineL2RowsByLabel(rowSets: any[][]): any[] {
    const combined = new Map<string, any>();
    rowSets.forEach((rows) => {
      rows.forEach((row) => {
        const labelKey = this.normalizeL2Label(row?.label);
        if (!labelKey) {
          return;
        }
        const existing = combined.get(labelKey) || {
          name: row.label,
          current: { rawTotal: 0, rawTop: 0, rawMid: 0, rawBottom: 0, sumOfRating: 0 },
          previous: { rawTotal: 0, rawTop: 0, rawMid: 0, rawBottom: 0, sumOfRating: 0 },
          chartSources: [],
        };
        ['current', 'previous'].forEach((bucket) => {
          existing[bucket].rawTotal += row[bucket]?.rawTotal || 0;
          existing[bucket].rawTop += row[bucket]?.rawTop || 0;
          existing[bucket].rawMid += row[bucket]?.rawMid || 0;
          existing[bucket].rawBottom += row[bucket]?.rawBottom || 0;
          existing[bucket].sumOfRating += row[bucket]?.sumOfRating || 0;
        });
        if (row.chartSource) {
          const sourceKey = `${row.chartSource.optionFieldId || row.chartSource.field_id}:${row.chartSource.field_value}`;
          const alreadyAdded = existing.chartSources.some(
            (source: any) => `${source.optionFieldId || source.field_id}:${source.field_value}` === sourceKey
          );
          if (!alreadyAdded) {
            existing.chartSources.push(row.chartSource);
          }
        }
        combined.set(labelKey, existing);
      });
    });
    return Array.from(combined.values())
      .filter((item: any) => this.getResponseCount(item.current) > 0)
      .map((item: any) => {
        const current = item.current;
        return {
          key: `l2-${item.name}`,
          name: item.name,
          chartSources: item.chartSources || [],
          ...this.buildCsatTableCells(current),
          deviations: this.mapDeviations(this.computeCombinedL2Deviations(current, item.previous)),
        };
      });
  }

  private computeCombinedL2Deviations(current: any, previous: any): any {
    if (!this.showComparisonFlag || typeof Helpers === 'undefined' || !Helpers.getFieldDeviation) {
      return {};
    }
    try {
      const deviations: any = {};
      const responsesDev = Helpers.getFieldDeviation(current.rawTotal, previous.rawTotal);
      const topDev = Helpers.getFieldDeviation(current.rawTop, previous.rawTop);
      const midDev = Helpers.getFieldDeviation(current.rawMid, previous.rawMid);
      const bottomDev = Helpers.getFieldDeviation(current.rawBottom, previous.rawBottom);
      if (shouldShowDeviation(responsesDev)) {
        deviations.responses = responsesDev;
      }
      if (shouldShowDeviation(topDev)) {
        deviations.top = topDev;
      }
      if (shouldShowDeviation(midDev)) {
        deviations.mid = midDev;
      }
      if (shouldShowDeviation(bottomDev)) {
        deviations.bottom = bottomDev;
      }
      if (current.rawTotal > 0 && previous.rawTotal > 0) {
        const currentRating = current.sumOfRating / current.rawTotal;
        const previousRating = previous.sumOfRating / previous.rawTotal;
        const ratingDev = Helpers.getFieldDeviation(currentRating, previousRating);
        if (shouldShowDeviation(ratingDev)) {
          deviations.rating = ratingDev;
        }
        const currentCsat = this.computeCsatScore(current);
        const previousCsat = this.computeCsatScore(previous);
        const csatDev = Helpers.getFieldDeviation(currentCsat, previousCsat);
        if (shouldShowDeviation(csatDev)) {
          deviations.csat = csatDev;
        }
      }
      return deviations;
    } catch (error) {
      console.error('Error computing custom combined L2 deviations:', error);
      return {};
    }
  }

  private async loadCombinedL2Table(): Promise<void> {
    const requestId = ++this.l2TableRequestId;
    this.l2TableLoader = true;
    this.l2TableError = false;
    this.l2Table = { header: [], row: [] };
    const headerLabels = this.getHeaderLabelsForGroups(this.groupHeaders);
    this.l2Table.header = this.buildSimpleTableHeader(
      headerLabels,
      this.viewTypeDropdownSelectedName || 'Cohort'
    );
    if (this.isSubtagsCohortSelected()) {
      // console.log('Selected group + Subtags L1 ids', {
      //   questionGroupId: this.viewDropdownFieldId,
      //   questionGroupName: this.selectedOption || this.viewByDropdown.selectedOption,
      //   cohortName: this.viewTypeDropdownSelectedName,
      //   cohortFieldId: this.viewTypeDropdownSelectedFieldId,
      //   l1Ids: this.getSelectedGroupL1Ids(),
      // });
    }
    const queryFields = _.uniqBy(this.getCohortQueryFields(), 'field_id');
    // console.log('Query Fields', queryFields);
    if (!queryFields.length) {
      this.l2TableLoader = false;
      this.l2TableError = true;
      return;
    }
    const payloads = queryFields.map((fieldItem: any) => this.buildComparisonPayloadForField(fieldItem));
    // console.log('Payloads', payloads);
    const settled = await this.fetchComparisonResponsesInParallel(payloads);
    // console.log('Settled', settled);
    if (requestId !== this.l2TableRequestId) {
      return;
    }
    const rowSets = settled
      .filter((result) => result.status === 'fulfilled' && result.value?.response?.message === 'Success')
      .map((result: any) => {
        const fieldItem =
          queryFields.find((item: any) => item.field_id === result.value.payload?.field_id) ||
          result.value.payload;
        return this.extractL2RowsFromComparisonResponse(result.value.response, fieldItem);
      });
    this.l2Table.row = this.combineL2RowsByLabel(rowSets);
    this.l2TableLoader = false;
    this.l2TableError = this.l2Table.row.length === 0;
  }

  private computeQuestionMetrics(item: any, detailedRating: any[]): any {
    const category = this.csatDistributionBands;
    const result = _.find(detailedRating, { field_id: item?.field_id });
    let sumOfRating = 0;
    let rawTop = 0;
    let rawMid = 0;
    let rawBottom = 0;
    (result?.detailed_rating_details || []).forEach((detail: any) => {
      const ratings = Number(detail?.rating || 0);
      const numberOfUsersRated = detail?.number_of_user_rated || 0;
      if (category.bottom.includes(ratings)) {
        rawBottom += numberOfUsersRated;
        sumOfRating += numberOfUsersRated * ratings;
      } else if (category.mid.includes(ratings)) {
        rawMid += numberOfUsersRated;
        sumOfRating += numberOfUsersRated * ratings;
      } else if (category.top.includes(ratings)) {
        rawTop += numberOfUsersRated;
        sumOfRating += numberOfUsersRated * ratings;
      }
    });
    const rawTotal = rawTop + rawMid + rawBottom;
    return {
      rawTotal,
      rawTop,
      rawMid,
      rawBottom,
      sumOfRating,
      rating: rawTotal > 0 ? parseFloat((sumOfRating / rawTotal).toFixed(2)) : 'N/A',
    };
  }

  private computeQuestionRowDeviations(item: any, currentDetailedRating: any[], previousDetailedRating: any[]): any {
    if (!this.showComparisonFlag || typeof Helpers === 'undefined' || !Helpers.getFieldDeviation) {
      return {};
    }
    try {
      const current = this.computeQuestionMetrics(item, currentDetailedRating);
      const previous = this.computeQuestionMetrics(item, previousDetailedRating);
      const parseRating = (value: number | string): number => {
        const parsed = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(parsed) ? 0 : parsed;
      };
      const deviations: any = {};
      const responsesDev = Helpers.getFieldDeviation(current.rawTotal, previous.rawTotal);
      const topDev = Helpers.getFieldDeviation(current.rawTop, previous.rawTop);
      const midDev = Helpers.getFieldDeviation(current.rawMid, previous.rawMid);
      const bottomDev = Helpers.getFieldDeviation(current.rawBottom, previous.rawBottom);
      if (shouldShowDeviation(responsesDev)) {
        deviations.responses = responsesDev;
      }
      if (shouldShowDeviation(topDev)) {
        deviations.top = topDev;
      }
      if (shouldShowDeviation(midDev)) {
        deviations.mid = midDev;
      }
      if (shouldShowDeviation(bottomDev)) {
        deviations.bottom = bottomDev;
      }
      if (current.rawTotal > 0) {
        const ratingDev = Helpers.getFieldDeviation(parseRating(current.rating), parseRating(previous.rating));
        if (shouldShowDeviation(ratingDev)) {
          deviations.rating = ratingDev;
        }
      }
      return deviations;
    } catch (error) {
      console.error('Error computing custom L2 question deviations:', error);
      return {};
    }
  }

  private buildGroupHeaders(): any[] {
    if (!this.isQuestionGroupMode) {
      const selectedQuestion = this.getDetailedFeedbackItems().find(
        (item: any) => item?.field_id === this.viewDropdownFieldId
      );
      if (!selectedQuestion) {
        return [];
      }
      return [{
        id: selectedQuestion.field_id,
        mainTableName: selectedQuestion?.text || selectedQuestion?.question || selectedQuestion.field_id,
        type: selectedQuestion?.type,
        main_rater_type: selectedQuestion?.main_rater_type,
        scale_rater_type: selectedQuestion?.scale_rater_type,
        is_primary_rating: selectedQuestion?.is_primary_rating,
        prop: selectedQuestion?.prop,
        subTableName: [{
          text: selectedQuestion?.text || selectedQuestion?.question,
          field_id: selectedQuestion?.field_id,
          prop: selectedQuestion?.prop,
        }],
      }];
    }
    const questionGroup: any = _.get(Helpers, 'brandDetails.dynamic_properties.question_group', null) || [];
    const detailedFeedbackItems: any = this.getDetailedFeedbackItems();
    const headers: any[] = [];
    questionGroup.forEach((item: any) => {
      if (item?.id !== this.viewDropdownFieldId) {
        return;
      }
      const detailedFeedbackItemsArray = _.filter(
        detailedFeedbackItems,
        (obj: any) => obj.dynamic_properties && _.some(obj.dynamic_properties.question_group, { id: item?.id })
      );
      if (!detailedFeedbackItemsArray.length) {
        return;
      }
      headers.push({
        id: item?.id,
        mainTableName: item?.name,
        type: detailedFeedbackItemsArray[0]?.type,
        main_rater_type: detailedFeedbackItemsArray[0]?.main_rater_type,
        scale_rater_type: detailedFeedbackItemsArray[0]?.scale_rater_type,
        is_primary_rating: detailedFeedbackItemsArray[0]?.is_primary_rating,
        prop: detailedFeedbackItemsArray[0]?.prop,
        subTableName: detailedFeedbackItemsArray.map((detailedFeedbackItem: any) => ({
          text: detailedFeedbackItem?.text || detailedFeedbackItem?.question,
          field_id: detailedFeedbackItem?.field_id,
          prop: detailedFeedbackItem?.prop,
        })),
      });
    });
    return headers;
  }

  private getResponseCount(metrics: any): number {
    const rawTotal = Number(metrics?.rawTotal);
    if (!isNaN(rawTotal) && rawTotal > 0) {
      return rawTotal;
    }
    return Number(metrics?.rawTop || 0) + Number(metrics?.rawMid || 0) + Number(metrics?.rawBottom || 0);
  }

  private computeCsatScore(metrics: any): number {
    const total = this.getResponseCount(metrics);
    if (!total) {
      return 0;
    }
    const tbPercent = (Number(metrics?.rawTop || 0) / total) * 100;
    const b3bPercent = (Number(metrics?.rawBottom || 0) / total) * 100;
    return Math.round(tbPercent - b3bPercent);
  }

  private formatCsatValue(metrics: any): string {
    if (!this.getResponseCount(metrics)) {
      return 'N/A';
    }
    return String(this.computeCsatScore(metrics));
  }

  private formatBandWithPercent(count: number, total: number): string {
    const formattedCount = this.responseAnalyticsService.formatNumber(count || 0);
    if (!total) {
      return `${formattedCount}(0.0%)`;
    }
    return `${formattedCount}(${((count / total) * 100).toFixed(1)}%)`;
  }

  private buildCsatTableCells(metrics: any): any {
    const rawTop = Number(metrics?.rawTop || 0);
    const rawMid = Number(metrics?.rawMid || 0);
    const rawBottom = Number(metrics?.rawBottom || 0);
    const total = rawTop + rawMid + rawBottom;
    return {
      csat: this.formatCsatValue({ rawTop, rawMid, rawBottom, rawTotal: total }),
      responses: this.responseAnalyticsService.formatNumber(total),
      top: this.formatBandWithPercent(rawTop, total),
      mid: this.formatBandWithPercent(rawMid, total),
      bottom: this.formatBandWithPercent(rawBottom, total),
    };
  }

  private buildSimpleTableHeader(headerLabels: any, firstColumnHeader: string = 'Question group'): any[] {
    const metricColumnWidth = this.isComparisonAvailable ? '150px' : '130px';
    return [
      {
        uniqueId: 'name',
        header: firstColumnHeader,
        width: '220px',
        style: { header: 'flex-start', row: 'flex-start' },
        isSorting: true,
        isClickable: true,
      },
      {
        uniqueId: 'csat',
        header: headerLabels?.csat || this.csatHeaderLabels.csat,
        width: metricColumnWidth,
        style: { header: 'flex-end', row: 'flex-end' },
        isSorting: true,
      },
      {
        uniqueId: 'responses',
        header: headerLabels?.responses || this.csatHeaderLabels.responses,
        width: metricColumnWidth,
        style: { header: 'flex-end', row: 'flex-end' },
        isSorting: true,
      },
      {
        uniqueId: 'top',
        header: headerLabels?.top || this.csatHeaderLabels.top,
        width: '140px',
        style: { header: 'flex-end', row: 'flex-end' },
        isSorting: true,
      },
      {
        uniqueId: 'mid',
        header: headerLabels?.mid || this.csatHeaderLabels.mid,
        width: '140px',
        style: { header: 'flex-end', row: 'flex-end' },
        isSorting: true,
      },
      {
        uniqueId: 'bottom',
        header: headerLabels?.bottom || this.csatHeaderLabels.bottom,
        width: '140px',
        style: { header: 'flex-end', row: 'flex-end' },
        isSorting: true,
      },
    ];
  }

  private getHeaderLabelsForGroups(_groups: any[]): any {
    return { ...this.csatHeaderLabels };
  }

  private prepareDataForAllGroup(header: any, response: any): any {
    const currentDetailedRating = _.get(_.find(response?.data?.response, { key: 'current' }), 'detailed_rating', []);
    return this.aggregateGroupMetrics(header, currentDetailedRating || []);
  }

  private aggregateGroupMetrics(header: any, detailedRating: any[]): any {
    const fieldIds = _.map(header.subTableName, 'field_id');
    const category = this.csatDistributionBands;
    let totalResponse = 0;
    let sumOfRating = 0;
    let totalTop = 0;
    let totalMid = 0;
    let totalBottom = 0;
    let rating: any = 0;
    const seprateRating: any[] = [];
    const hasMid = true;
    for (const key in header?.prop || {}) {
      seprateRating.push({ ...header.prop[key], count: 0, percentage: '0%' });
    }
    fieldIds.forEach((fieldId: string) => {
      const result = _.find(detailedRating, { field_id: fieldId });
      if (result?.detailed_rating_details?.length > 0) {
        result.detailed_rating_details.forEach((detailedRatingDetail: any) => {
          const ratings = Number(detailedRatingDetail.rating || 0);
          const numberOfUsersRated = detailedRatingDetail?.number_of_user_rated || 0;
          if (category.bottom.includes(ratings)) {
            totalBottom += numberOfUsersRated;
            sumOfRating += numberOfUsersRated * ratings;
          } else if (category.mid.includes(ratings)) {
            totalMid += numberOfUsersRated;
            sumOfRating += numberOfUsersRated * ratings;
          } else if (category.top.includes(ratings)) {
            totalTop += numberOfUsersRated;
            sumOfRating += numberOfUsersRated * ratings;
          }
        });
      }
    });
    totalResponse = totalTop + totalMid + totalBottom;
    rating = totalResponse > 0 ? parseFloat((sumOfRating / totalResponse).toFixed(2)) : 'N/A';
    fieldIds.forEach((fieldId: string) => {
      const result = _.find(detailedRating, { field_id: fieldId });
      if (result?.detailed_rating_details?.length > 0) {
        result.detailed_rating_details.forEach((detailedRatingDetail: any) => {
          const ratingObj = seprateRating.find((items: any) => items.value == detailedRatingDetail.rating);
          if (ratingObj) {
            ratingObj.count += detailedRatingDetail?.number_of_user_rated || 0;
            ratingObj.percentage =
              totalResponse > 0 ? (((ratingObj.count || 0) / totalResponse) * 100).toFixed(1) + '%' : '0%';
          }
        });
      }
    });
    return {
      rawTotal: totalResponse,
      rawTop: totalTop,
      rawMid: totalMid,
      rawBottom: totalBottom,
      sumOfRating,
      rating,
      hasMid,
      seprateRating,
    };
  }

  private computeGroupRowDeviations(header: any, response: any): any {
    if (!this.showComparisonFlag || typeof Helpers === 'undefined' || !Helpers.getFieldDeviation) {
      return {};
    }
    try {
      const currentDetailedRating = _.get(_.find(response?.data?.response, { key: 'current' }), 'detailed_rating', []);
      const previousDetailedRating = _.get(_.find(response?.data?.response, { key: 'previous' }), 'detailed_rating', []);
      const current = this.aggregateGroupMetrics(header, currentDetailedRating || []);
      const previous = this.aggregateGroupMetrics(header, previousDetailedRating || []);
      const parseRating = (value: number | string): number | null => {
        if (value === 'N/A' || value == null || value === '') {
          return null;
        }
        const parsed = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(parsed) ? null : parsed;
      };
      const deviations: any = {};
      const responsesDev = Helpers.getFieldDeviation(current.rawTotal, previous.rawTotal);
      const topDev = Helpers.getFieldDeviation(current.rawTop, previous.rawTop);
      const bottomDev = Helpers.getFieldDeviation(current.rawBottom, previous.rawBottom);
      if (shouldShowDeviation(responsesDev)) {
        deviations.responses = responsesDev;
      }
      if (shouldShowDeviation(topDev)) {
        deviations.top = topDev;
      }
      if (shouldShowDeviation(bottomDev)) {
        deviations.bottom = bottomDev;
      }
      if (current.rawTotal > 0) {
        const ratingDev = Helpers.getFieldDeviation(parseRating(current.rating) ?? 0, parseRating(previous.rating) ?? 0);
        if (shouldShowDeviation(ratingDev)) {
          deviations.rating = ratingDev;
        }
      }
      if (current.hasMid) {
        const midDev = Helpers.getFieldDeviation(current.rawMid, previous.rawMid);
        if (shouldShowDeviation(midDev)) {
          deviations.mid = midDev;
        }
      }
      if (current.rawTotal > 0 && previous.rawTotal > 0) {
        const csatDev = Helpers.getFieldDeviation(this.computeCsatScore(current), this.computeCsatScore(previous));
        if (shouldShowDeviation(csatDev)) {
          deviations.csat = csatDev;
        }
      }
      return deviations;
    } catch (error) {
      console.error('Error computing custom group row deviations:', error);
      return {};
    }
  }

  private mapDeviations(deviations: any): any {
    return deviations || {};
  }

  private getActiveGroupId(): any {
    if (this.viewDropdownFieldId) {
      return this.viewDropdownFieldId;
    }
    if (this.textHighLightId && this.scoreTable.row.some((row: any) => row.key === this.textHighLightId)) {
      return this.textHighLightId;
    }
    return this.scoreTable.row[0]?.key;
  }

  private getSelectedGroupQuestion(): any {
    const groupId = this.getActiveGroupId();
    const detailedFeedbackItems = this.getDetailedFeedbackItems();
    if (!this.isQuestionGroupMode) {
      return detailedFeedbackItems.find((item: any) => item?.field_id === groupId);
    }
    return detailedFeedbackItems.find(
      (item: any) => item.dynamic_properties?.question_group?.some((group: any) => group.id === groupId)
    );
  }

  private callApiForLineChart(): void {
    if (this.lineChartTrendType == null || this.lineChartTrendType === '') {
      return;
    }
    this.lineChartLoader = true;
    this.fetchTrendData(this.lineChartTrendType).then((data) => {
      if (!data) {
        this.lineChartLoader = false;
        this.lineChartError = true;
        return;
      }
      this.lineChartDataCache = data;
      this.prepareLineChartData(data);
    }).catch(() => {
      this.lineChartLoader = false;
      this.lineChartError = true;
    });
  }

  private callApiForBarChart(): void {
    if (this.barChartTrendType == null || this.barChartTrendType === '') {
      return;
    }
    this.barChartLoader = true;
    this.fetchTrendData(this.barChartTrendType).then((data) => {
      if (!data || _.size(data) === 0) {
        this.barChartLoader = false;
        this.barChartError = true;
        return;
      }
      this.barChartDataCache = data;
      this.barChartLoader = false;
      this.barChartError = false;
      this.barChartData = this.generateBarChartConfig(data);
    }).catch(() => {
      this.barChartLoader = false;
      this.barChartError = true;
    });
  }

  private buildChartFilter(source: any): any {
    if (!source) {
      return null;
    }
    const possibleValues = source.possible_values || [];
    const resolvedFieldId = this.resolveMultiSelectFieldId(
      source.field_id,
      source.field_value,
      possibleValues
    );
    if (source.field_sub_type === 'multi-select') {
      return {
        field_id: resolvedFieldId || source.optionFieldId || source.field_id,
        field_type: 'response',
        field_value: source.field_value,
        field_sub_type: 'multi-select',
      };
    }
    const filter: any = {
      field_id: source.field_id,
      field_type: source.field_type || 'tag',
      field_value: source.field_value,
    };
    if (source.field_sub_type && source.field_sub_type !== 'tag') {
      filter.field_sub_type = source.field_sub_type;
    }
    if (source.field_data_type) {
      filter.field_data_type = source.field_data_type;
    }
    return filter;
  }

  private resolveMultiSelectFieldId(fieldId: string, fieldValue: any, possibleValues: any[]): string {
    if (!Array.isArray(possibleValues)) {
      return fieldId;
    }
    const matched = possibleValues.find(
      (item: any) => item.value == fieldValue || item.field_id == fieldValue
    );
    return matched?.field_id ?? fieldId;
  }

  private async fetchTrendData(trendType: any): Promise<any[] | null> {
    const sources = this.selectedChartFilters?.length ? this.selectedChartFilters : [null];
    const payloads = sources
      .map((source: any) => this.buildTrendPayload(trendType, source))
      .filter((payload: any) => !!payload);
    if (!payloads.length) {
      return null;
    }
    const settled = await Promise.allSettled(
      payloads.map((payload: any) =>
        firstValueFrom(
          this.responseAnalyticsService.getTrendDataRatingSummary(payload).pipe(takeUntil(this.destroy$))
        )
      )
    );
    const series = settled
      .filter((result: any) => result.status === 'fulfilled' && result.value?.message === 'Success')
      .map((result: any) => result.value?.data?.response || []);
    if (!series.length) {
      return null;
    }
    return this.mergeTrendResponses(series);
  }

  private mergeTrendResponses(seriesList: any[][]): any[] {
    if (seriesList.length === 1) {
      return seriesList[0] || [];
    }
    const byTime = new Map<string, any>();
    seriesList.forEach((series) => {
      (series || []).forEach((item: any) => {
        const timeKey = String(item?.time ?? '');
        if (!byTime.has(timeKey)) {
          byTime.set(timeKey, { ...item, detailed_rating: _.cloneDeep(item?.detailed_rating || []) });
          return;
        }
        const existing = byTime.get(timeKey);
        existing.detailed_rating = this.mergeDetailedRatings(
          existing.detailed_rating || [],
          item?.detailed_rating || []
        );
      });
    });
    return Array.from(byTime.values()).sort((left: any, right: any) =>
      String(left?.time || '').localeCompare(String(right?.time || ''))
    );
  }

  private mergeDetailedRatings(target: any[], source: any[]): any[] {
    const merged = _.cloneDeep(target || []);
    (source || []).forEach((src: any) => {
      let dest = merged.find((item: any) => item?.field_id === src?.field_id);
      if (!dest) {
        merged.push(_.cloneDeep(src));
        return;
      }
      const destDetails = dest.detailed_rating || dest.detailed_rating_details || [];
      const srcDetails = src.detailed_rating || src.detailed_rating_details || [];
      const detailsByRating = new Map<string, any>();
      destDetails.forEach((detail: any) => {
        detailsByRating.set(String(detail?.rating ?? detail?.key), { ...detail });
      });
      srcDetails.forEach((detail: any) => {
        const ratingKey = String(detail?.rating ?? detail?.key);
        const existing = detailsByRating.get(ratingKey);
        const count = detail?.number_of_user_rated || detail?.number_of_users_rated || detail?.doc_count || 0;
        if (!existing) {
          detailsByRating.set(ratingKey, { ...detail });
          return;
        }
        existing.number_of_user_rated =
          (existing.number_of_user_rated || existing.number_of_users_rated || existing.doc_count || 0) + count;
      });
      dest.detailed_rating = Array.from(detailsByRating.values());
      dest.detailed_rating_details = dest.detailed_rating;
    });
    return merged;
  }

  private buildTrendPayload(trendType: any, chartSource: any = null): any {
    const mainRaterType = _.get(Helpers, 'brandDetails.main_rater_type', 2);
    if (!mainRaterType || mainRaterType == 2) {
      return null;
    }
    const chartFilter = this.buildChartFilter(chartSource);
    let payload: any = {
      brand_id: _.get(Helpers, 'brandDetails._id', null),
      group_id: null,
      main_rater_type: mainRaterType,
      trend_type: trendType,
      from: _.get(Helpers, 'datePicker.startDate', null),
      to: _.get(Helpers, 'datePicker.endDate', null),
      caller: 1,
      use_es: true,
      questions: null,
      shop_id: null,
      shop_id_list: [],
      date_filter_type: _.get(Helpers, 'datePicker.dateFilterType', 1),
      universal_filter: _.get(Helpers, 'universalFilter', null),
      hierarchy_stack: _.get(Helpers, 'hierarchyStack', null),
      group_alias_key: this.getGroupAliasKey(),
      filters: chartFilter ? [chartFilter] : [],
    };
    payload = _.omitBy(payload, _.isNil);
    this.applyComparisonApiDateRange(payload);
    return payload;
  }

  private prepareLineChartData(data: any): void {
    const detailedFeedbackItem = this.getSelectedGroupQuestion();
    if (!detailedFeedbackItem) {
      this.lineChartLoader = false;
      this.lineChartError = true;
      return;
    }
    const headerLabels = this.getHeaderLabels(
      detailedFeedbackItem?.main_rater_type,
      detailedFeedbackItem?.scale_rater_type,
      false
    );
    this.lineChartDisplayName = headerLabels?.display || 'CSAT';
    this.setMinMaxValueForLineChart();
    const labels: string[] = [];
    const chartData: number[] = [];
    (data || []).forEach((item: any) => {
      const ratingDetails = this.getGroupedLineChartRatingDetails(item);
      if (ratingDetails.length > 0) {
        const ratingData = this.getRatingBasedData(
          detailedFeedbackItem.field_id,
          ratingDetails,
          detailedFeedbackItem?.main_rater_type,
          detailedFeedbackItem?.scale_rater_type,
          detailedFeedbackItem?.is_primary_rating
        );
        if (Number(ratingData?.rawTotal) > 0) {
          labels.push(this.prepareLabelsForChart(item?.time, this.lineChartTrendType));
          chartData.push(this.computeCsatScore(ratingData));
        }
      }
    });
    this.lineChartData = {
      labels,
      xText: this.lineChartDisplay,
      yText: headerLabels?.display || 'CSAT',
      yTextDisplayName: 'CSAT (%)',
      data: chartData,
    };
    this.lineChartLoader = false;
    this.lineChartError = chartData.length === 0;
  }

  private generateBarChartConfig(data: any): any {
    const detailedFeedbackItem = this.getSelectedGroupQuestion();
    const headerLabels = this.getHeaderLabels(
      detailedFeedbackItem?.main_rater_type,
      detailedFeedbackItem?.scale_rater_type,
      false
    );
    const labels: string[] = [];
    const dataset: any = ['top', 'mid', 'bottom']
      .filter((level) => headerLabels?.[level])
      .map((level) => ({
        label: headerLabels[level],
        data: [],
        backgroundColor: this.getColorByLevel(level),
        maxBarThickness: 60,
      }));
    (data || []).forEach((item: any) => {
      const ratingDetails = this.getGroupedLineChartRatingDetails(item);
      if (!ratingDetails.length) {
        return;
      }
      const ratingData = this.getRatingBasedData(
        detailedFeedbackItem?.field_id,
        ratingDetails,
        detailedFeedbackItem?.main_rater_type,
        detailedFeedbackItem?.scale_rater_type,
        detailedFeedbackItem?.is_primary_rating
      );
      if (!ratingData?.rawTotal) {
        return;
      }
      labels.push(this.prepareLabelsForChart(item?.time, this.barChartTrendType));
      dataset.forEach((series: any) => {
        const raw =
          series.label === headerLabels.top
            ? ratingData.rawTop
            : series.label === headerLabels.mid
              ? ratingData.rawMid
              : ratingData.rawBottom;
        series.data.push(this.computeValue(raw, ratingData.rawTotal, this.isBarChartTypePercentage));
      });
    });
    return {
      labels,
      xText: this.barChartDisplay,
      dataset,
      showPercentage: this.isBarChartTypePercentage,
    };
  }

  private getGroupedLineChartRatingDetails(item: any): any[] {
    const groupId = this.getActiveGroupId();
    const groupQuestions = _.filter(
      Helpers.brandDetails?.detailed_feedback_items,
      (obj: any) => {
        if (obj.type !== 'rating') {
          return false;
        }
        if (!this.isQuestionGroupMode) {
          return obj.field_id === groupId;
        }
        return obj.dynamic_properties && _.some(obj.dynamic_properties.question_group, { id: groupId });
      }
    );
    const aggregated: Record<number, number> = {};
    groupQuestions.forEach((question: any) => {
      const fieldRating = item?.detailed_rating?.find((dr: any) => dr.field_id === question.field_id);
      const result = fieldRating?.detailed_rating || fieldRating?.detailed_rating_details;
      if (!result) {
        return;
      }
      const ratingEntries = Array.isArray(result) ? result : Object.values(result);
      ratingEntries.forEach((detail: any) => {
        const rating = Number(detail?.rating ?? detail?.key);
        const count = detail?.number_of_user_rated || detail?.number_of_users_rated || detail?.doc_count || 0;
        if (!isNaN(rating)) {
          aggregated[rating] = (aggregated[rating] || 0) + count;
        }
      });
    });
    return Object.entries(aggregated).map(([rating, count]) => ({
      rating: Number(rating),
      number_of_user_rated: count,
    }));
  }

  private getRatingBasedData(
    fieldId: any,
    data: any,
    mainRaterType: string | number,
    scalerRaterType: string | number,
    isPrimaryRating: boolean
  ): any {
    let totalTop = 0;
    let totalMid = 0;
    let totalBottom = 0;
    let sumOfPrimaryRating = 0;
    const category = this.csatDistributionBands;
    for (const key in data) {
      const ratingValue = Number(data[key].rating || data[key]?.key || 0);
      const numberOfUsersRated = data[key]?.number_of_user_rated || data[key]?.number_of_users_rated || data[key]?.doc_count || 0;
      if (category.bottom.includes(ratingValue)) {
        totalBottom += numberOfUsersRated;
        sumOfPrimaryRating += numberOfUsersRated * ratingValue;
      } else if (category.mid.includes(ratingValue)) {
        totalMid += numberOfUsersRated;
        sumOfPrimaryRating += numberOfUsersRated * ratingValue;
      } else if (category.top.includes(ratingValue)) {
        totalTop += numberOfUsersRated;
        sumOfPrimaryRating += numberOfUsersRated * ratingValue;
      }
    }
    const totalResponse = totalTop + totalMid + totalBottom;
    const rating: any = totalResponse > 0 ? (sumOfPrimaryRating / totalResponse).toFixed(2) : 'N/A';
    return {
      rating,
      rawTotal: totalResponse,
      rawTop: totalTop,
      rawMid: totalMid,
      rawBottom: totalBottom,
      fieldId,
      isPrimaryRating,
      mainRaterType,
    };
  }

  private downloadTable(): void {
    if (!this.scoreTable.row.length) {
      this.toastService.showPopup('WARN', 'No data is available for download with the selected filters.', '', 5000);
      return;
    }
    const downloadTables: any[] = [{ columns: this.scoreTable.header, rows: this.scoreTable.row }];
    if (this.l2Table.row.length) {
      downloadTables.push({ columns: this.l2Table.header, rows: this.l2Table.row });
    }
    this.responseAnalyticsService.prepareAndDownloadData({
      dataKey: 'uniqueId',
      headerKey: 'header',
      data: downloadTables,
      fileDetails: {
        title: `Score Custom Table - ${this.viewByDropdown.selectedOption}`,
        fileName: `Score Custom Table - ${this.viewByDropdown.selectedOption}`,
        accountName: Helpers?.accountDetails?.display_name || Helpers?.accountDetails?.name,
        brandName: Helpers?.brandDetails?.name,
        interaction: this.selectedOption || this.viewByDropdown.selectedOption,
      },
    });
  }

  private downloadChart(cardName: string): void {
    const cache = cardName === 'barChart' ? this.barChartDataCache : this.lineChartDataCache;
    if (!cache || cache.length === 0) {
      this.toastService.showPopup('WARN', 'No data is available for download with the selected filters.', '', 5000);
      return;
    }
    const detailedFeedbackItem = this.getSelectedGroupQuestion();
    const headerLabels = this.getHeaderLabels(
      detailedFeedbackItem?.main_rater_type,
      detailedFeedbackItem?.scale_rater_type,
      false
    );
    const trendType = cardName === 'barChart' ? this.barChartTrendType : this.lineChartTrendType;
    const rows = [
      ['Date', headerLabels?.display || 'CSAT', 'Responses', headerLabels?.top, headerLabels?.mid, headerLabels?.bottom]
        .filter(Boolean)
        .join(','),
    ];
    cache.forEach((item: any) => {
      const ratingDetails = this.getGroupedLineChartRatingDetails(item);
      if (!ratingDetails.length) {
        return;
      }
      const ratingData = this.getRatingBasedData(
        detailedFeedbackItem?.field_id,
        ratingDetails,
        detailedFeedbackItem?.main_rater_type,
        detailedFeedbackItem?.scale_rater_type,
        detailedFeedbackItem?.is_primary_rating
      );
      const cols = [
        this.prepareLabelsForChart(item?.time, trendType),
        cardName === 'lineChart'
          ? this.computeCsatScore(ratingData)
          : ratingData.rating,
        ratingData.rawTotal,
        ratingData.rawTop,
        headerLabels?.mid ? ratingData.rawMid : null,
        ratingData.rawBottom,
      ].filter((value) => value !== null);
      rows.push(cols.join(','));
    });
    const tableInfo = this.prepareTableInfoToCSV(cardName);
    this.sendReportForDownload(`${tableInfo}\n\n${rows.join('\n')}`, cardName);
  }

  private prepareTableInfoToCSV(cardName: string): string {
    const obj = {
      'Report name :': `Score Custom ${cardName === 'barChart' ? 'Response' : 'Rating'} Trend Report`,
      'Report Date :': `${moment(_.get(Helpers, "datePicker.formatedStartDate"), "DD MMM' YY").format('DD-MMM-YYYY')} - ${moment(_.get(Helpers, "datePicker.formatedEndDate"), "DD MMM' YY").format('DD-MMM-YYYY')}`,
      'For Account :': Helpers?.accountDetails?.display_name || Helpers?.accountDetails?.name,
      'For Project :': Helpers?.brandDetails?.display_name || Helpers?.brandDetails?.name,
      'Interaction :': this.selectedOption || this.viewByDropdown.selectedOption,
      'Downloaded On :': moment().format('DD-MMM-YYYY hh:mm:ss a'),
      'Downloaded By :': Helpers?.managerDetails?.manager?.email_address,
    };
    return Object.entries(obj)
      .map(([key, value]) => {
        let nextValue: any = value;
        if (typeof nextValue === 'string' && (nextValue.includes(',') || nextValue.includes('\n') || nextValue.includes('"'))) {
          nextValue = `"${nextValue.replace(/"/g, '""')}"`;
        }
        return `${key},${nextValue}`;
      })
      .join('\n');
  }

  private sendReportForDownload(processedFile: string, cardName: string): void {
    const name = cardName === 'barChart' ? 'Response Trend' : 'Rating Trend';
    const uri = 'data:text/csv;charset=utf-8,%EF%BB%BF' + encodeURIComponent(processedFile);
    this.downloadService.updateList({
      _id: this.downloadService.getDownloadId(),
      filename: `Score Custom ${name} Report - ${moment().format('DD-MMM-YYYY')}`,
      orignal_filename: `Score Custom ${name} Report - ${moment().format('DD-MMM-YYYY')}.csv`,
      fileType: 'csv',
      dataSource: 'dashboard',
      progress: 100,
      uri,
    });
    this.downloadService.showDownloadSideBar(true);
  }

  private getHeaderLabels(_mainRaterType?: string | number, _scalerRaterType?: string | number, _isPrimaryRating?: boolean): any {
    return { ...this.csatHeaderLabels };
  }

  private getGroupAliasKey(): any {
    let hierarchyStack = _.get(Helpers, 'hierarchyStack', []) || [];
    hierarchyStack = hierarchyStack.filter((stack: any) => stack?.field_id != 'touchpoints');
    if (hierarchyStack?.length) {
      const currentLevel = hierarchyStack[hierarchyStack.length - 1]?.field_id;
      if (currentLevel) {
        const hierarchyListAlias = _.get(Helpers, 'brandDetails.hierarchy_list_alias', {});
        const alias = _.find(hierarchyListAlias, (item: any) => item?.values?.includes(currentLevel));
        if (alias) {
          const values = alias?.values;
          const nextLevelIndex = values.indexOf(currentLevel) + 1;
          return values[nextLevelIndex] || null;
        }
      }
    } else {
      const hierarchyFilterMapping = Helpers?.hierarchyFilterMapping;
      return hierarchyFilterMapping?.[1]?.key;
    }
  }

  private prepareLabelsForChart(date: any, trendType: any): any {
    if (trendType == 0) {
      return moment.utc(date).format('hh:mm A');
    }
    if (trendType == 1 || trendType == 2) {
      return moment.utc(date).format("DD MMM 'YY");
    }
    if (trendType == 3) {
      return moment.utc(date).format("MMM 'YY");
    }
    if (trendType == 4) {
      return moment.utc(date).format('MMM') + '-' + moment.utc(date).endOf('quarter').format('MMM') + ' ' + moment.utc(date).format('YYYY');
    }
    if (trendType == 5) {
      return moment.utc(date).format('YYYY');
    }
    return '';
  }

  private setMinMaxValueForLineChart(): void {
    this.lineChartPlugins = {
      ...this.lineChartPlugins,
      useCustomMinMax: true,
      min: -100,
      max: 100,
    };
  }

  private computeValue(value: number, total: number, percentage: boolean): number | string {
    if (!percentage) {
      return value || 0;
    }
    return total > 0 ? ((value / total) * 100).toFixed(1) : '0';
  }

  private getColorByLevel(level: string): string {
    const colors: any = { top: '#0B7139', mid: '#FAB416', bottom: '#ED2424' };
    return colors[level] || '#000000';
  }

  private getProjectName(): string {
    return _.get(Helpers, 'brandDetails.name', '');
  }

  private runAfterCurrentChangeDetection(callback: () => void): void {
    Promise.resolve().then(() => {
      callback();
      this.changeDetectorRef.markForCheck();
    });
  }

  private refreshComparisonLabels(): void {
    this.dateType =
      _.get(Helpers, 'datePicker.trendType') ||
      _.get(Helpers, 'datePicker.selectedTrend') ||
      'period';
    this.comparisonPeriodLabel = getComparisonPeriodLabel();
    this.isComparisonAvailable = isComparisonAvailable();
    if (!this.isComparisonAvailable) {
      this.showComparisonFlag = false;
      setShowComparisonFlag(false);
    } else {
      this.showComparisonFlag = getShowComparisonFlag();
    }
  }

  private applyComparisonApiDateRange(payload: any): void {
    if (!payload) {
      return;
    }
    applyComparisonApiDateRange(payload, _.get(Helpers, 'datePicker', null) ?? Helpers?.datePicker);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
